import { Link } from '@tanstack/react-router';
import { ArrowUpRight, Check, CreditCard, Lock } from 'lucide-react';
import { useEffect } from 'react';
import { billing } from '@buildbee/domain';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  ErrorState,
  LoadingState,
  Status,
  cn,
  formatDate,
  useToast,
} from '@buildbee/ui';
import { statusTone } from '../../status-tone';
import { errorMessage, trpc, type RouterOutputs } from '../../../lib/trpc';

type Billing = RouterOutputs['workspace']['billing']['get'];
type Plan = Billing['plans'][number];

// ---------------------------------------------------------------------------
// Feature gating helpers (the API enforces the same rules; these only explain them)
// ---------------------------------------------------------------------------

/** Which plan feature a web route needs, mirroring billing.FEATURE_PATHS on the API. */
export function planFeatureForPathname(pathname: string): billing.PlanFeature | null {
  if (pathname.startsWith('/financials')) return 'financials';
  if (pathname.startsWith('/projects/bids') || /^\/projects\/[^/]+\/bids(\/|$)/.test(pathname)) return 'bids';
  if (pathname.startsWith('/integrations/calendar') || pathname.startsWith('/integrations/storage')) return 'integrations';
  if (pathname.startsWith('/integrations/api-keys') || pathname.startsWith('/integrations/webhooks')) return 'api';
  return null;
}

/** How a workspace is billed, for upgrade prompts: Hive-managed workspaces upgrade in Hive. */
export type BillingContext = { billingMode: billing.BillingMode; billingUrl: string | null };

/** "Upgrade in Hive": a link to Hive's billing page (plain text when HIVE_WEB_URL is not configured). */
export function UpgradeInHive({ billingUrl, label = 'Upgrade in Hive', className }: { billingUrl: string | null; label?: string; className?: string }) {
  if (!billingUrl) return <span className={className}>{label}</span>;
  return (
    <a href={billingUrl} className={className}>
      {label}
    </a>
  );
}

/** Shown instead of a page the workspace's plan does not include. */
export function UpgradeRequired({ feature, billingContext }: { feature: billing.PlanFeature; billingContext?: BillingContext }) {
  if (billingContext?.billingMode === 'hive') {
    return (
      <div className="mx-auto max-w-xl py-16 text-center">
        <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-muted">
          <Lock className="size-5 text-muted-foreground" aria-hidden />
        </div>
        <h1 className="text-[22px] font-semibold tracking-[-0.02em]">{billing.PLAN_FEATURE_LABELS[feature]} is a Premium feature</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          This workspace is billed through Hive. Premium adds unlimited projects, budgets and change orders, bids, integrations, the open
          API and AI alerts. Your data is kept if you switch plans.
        </p>
        {billingContext.billingUrl ? (
          <Button asChild className="mt-6">
            <a href={billingContext.billingUrl}>
              Upgrade in Hive <ArrowUpRight />
            </a>
          </Button>
        ) : (
          <p className="mt-6 text-sm font-medium">Upgrade in Hive.</p>
        )}
      </div>
    );
  }
  return (
    <div className="mx-auto max-w-xl py-16 text-center">
      <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-muted">
        <Lock className="size-5 text-muted-foreground" aria-hidden />
      </div>
      <h1 className="text-[22px] font-semibold tracking-[-0.02em]">{billing.PLAN_FEATURE_LABELS[feature]} is a Premium feature</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Premium is {billing.formatPrice(billing.PLANS.premium)} per company a month and adds unlimited projects, budgets, change
        orders, subcontracts and invoicing. Your data is kept if you switch plans.
      </p>
      <Button asChild className="mt-6">
        <Link to="/settings/billing">
          See plans <ArrowUpRight />
        </Link>
      </Button>
    </div>
  );
}

/** Trial countdown, payment problems and lapsed subscriptions, above every page. */
export function PlanBanner({
  entitlements,
  billingContext,
}: {
  entitlements: { state: string; plan: string; trialDaysLeft: number | null };
  billingContext?: BillingContext;
}) {
  const e = entitlements;
  if (billingContext?.billingMode === 'hive') return <HivePlanBanner entitlements={e} billingUrl={billingContext.billingUrl} />;
  let variant: 'info' | 'warning' | 'destructive' | null = null;
  let text = '';
  if (e.state === 'suspended') {
    variant = 'destructive';
    text = 'BuildBee support has suspended this workspace, so it is read-only. Contact support to restore it; your data is safe and can be exported.';
  } else if (e.state === 'inactive') {
    variant = 'destructive';
    text = e.plan === 'trial' ? 'Your free trial has ended. The workspace is read-only until you choose a plan.' : 'Your subscription has ended. The workspace is read-only until you choose a plan.';
  } else if (e.state === 'past_due') {
    variant = 'warning';
    text = 'Your last payment failed. Update your card to keep BuildBee running.';
  } else if (e.state === 'trialing' && e.trialDaysLeft !== null && e.trialDaysLeft <= 7) {
    variant = 'info';
    text = `Your free trial ends in ${e.trialDaysLeft} day${e.trialDaysLeft === 1 ? '' : 's'}. You have full Premium access until then.`;
  }
  if (!variant) return null;
  return (
    <Alert variant={variant} className="mb-4 flex flex-wrap items-center justify-between gap-2">
      <span>{text}</span>
      {e.state !== 'suspended' && (
        <Link to="/settings/billing" className="font-medium underline underline-offset-4">
          {e.state === 'past_due' ? 'Update billing' : 'Choose a plan'}
        </Link>
      )}
    </Alert>
  );
}

/** PlanBanner for a Hive-managed workspace: every billing action happens in Hive. */
function HivePlanBanner({ entitlements: e, billingUrl }: { entitlements: { state: string; trialDaysLeft: number | null }; billingUrl: string | null }) {
  let variant: 'info' | 'warning' | 'destructive' | null = null;
  let text = '';
  if (e.state === 'suspended') {
    variant = 'destructive';
    text = 'This workspace is suspended in Hive, so it is read-only. Your data is safe and can be exported.';
  } else if (e.state === 'inactive') {
    variant = 'destructive';
    text = 'Your BuildBee subscription in Hive has ended. The workspace is read-only until the plan is renewed in Hive.';
  } else if (e.state === 'past_due') {
    variant = 'warning';
    text = 'Your last Hive payment failed. Update billing in Hive to keep BuildBee running.';
  } else if (e.state === 'trialing' && e.trialDaysLeft !== null && e.trialDaysLeft <= 7) {
    variant = 'info';
    text = `Your free trial ends in ${e.trialDaysLeft} day${e.trialDaysLeft === 1 ? '' : 's'}.`;
  }
  if (!variant) return null;
  return (
    <Alert variant={variant} className="mb-4 flex flex-wrap items-center justify-between gap-2">
      <span>{text}</span>
      <UpgradeInHive billingUrl={billingUrl} label="Manage in Hive" className="font-medium underline underline-offset-4" />
    </Alert>
  );
}

// ---------------------------------------------------------------------------
// Settings → Billing
// ---------------------------------------------------------------------------

const STATE_LABEL: Record<string, { label: string; variant: 'info' | 'success' | 'warning' | 'destructive' }> = {
  trialing: { label: 'Free trial', variant: 'info' },
  active: { label: 'Active', variant: 'success' },
  past_due: { label: 'Payment failed', variant: 'warning' },
  inactive: { label: 'Inactive', variant: 'destructive' },
  suspended: { label: 'Suspended', variant: 'destructive' },
};

export function BillingSettingsPage({ checkout }: { checkout?: 'success' | 'cancelled' }) {
  const query = trpc.workspace.billing.get.useQuery();
  const utils = trpc.useUtils();

  // Stripe redirects back before its webhook has always landed: poll briefly after a successful checkout.
  useEffect(() => {
    if (checkout !== 'success') return;
    const timer = setInterval(() => void utils.workspace.billing.get.invalidate(), 2000);
    const stop = setTimeout(() => clearInterval(timer), 20_000);
    return () => {
      clearInterval(timer);
      clearTimeout(stop);
    };
  }, [checkout, utils]);

  if (query.isLoading) return <LoadingState />;
  if (query.error || !query.data) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  const b = query.data;
  if (b.billingMode === 'hive') return <HiveBilling b={b} />;

  return (
    <div className="space-y-6">
      {checkout === 'success' && <Alert variant="success">Thanks! Your subscription is being activated; this page updates in a few seconds.</Alert>}
      {checkout === 'cancelled' && <Alert variant="info">Checkout was cancelled. Nothing was charged.</Alert>}
      <CurrentPlanCard b={b} />
      <div className="grid gap-4 md:grid-cols-2">
        {b.plans.map((p) => (
          <PlanCard key={p.id} plan={p} b={b} />
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        Prices are in {billing.PLANS.basic.currency.toUpperCase()} (Canadian dollars) per company per month, billed monthly, and exclude applicable taxes. No annual contract: cancel anytime
        from Manage billing. Switching between plans is prorated.
      </p>
    </div>
  );
}

/** Projects used against the plan's yearly allowance. */
function ProjectUsage({ b }: { b: Billing }) {
  const limit = b.projectsPerYear;
  const used = b.projectsInWindow;
  return (
    <div className="text-sm">
      <div className="flex justify-between">
        <span className="text-muted-foreground">Projects created in the last 12 months</span>
        <span className="font-medium">
          {used}
          {limit !== null ? ` of ${limit}` : ' (unlimited)'}
        </span>
      </div>
      {limit !== null && (
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuemin={0} aria-valuemax={limit} aria-valuenow={used}>
          <div className={cn('h-full', used >= limit ? 'bg-destructive' : 'bg-foreground')} style={{ width: `${Math.min(100, (used / limit) * 100)}%` }} />
        </div>
      )}
    </div>
  );
}

/**
 * Billing page of a Hive-managed workspace: the plan from Hive's last push and a "Manage in Hive"
 * link. No Stripe Checkout or portal (the API refuses them too).
 */
function HiveBilling({ b }: { b: Billing }) {
  const state = STATE_LABEL[b.state] ?? STATE_LABEL.active!;
  const billingUrl = b.hive?.links?.billingUrl ?? null;
  const addons = b.hive?.addons ?? [];
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {b.planName} plan <Status tone={statusTone(b.state)}>{state.label}</Status>
          </CardTitle>
          <CardDescription>
            {b.state === 'trialing' && b.trialEndsAt
              ? `Free trial until ${formatDate(b.trialEndsAt)}${b.trialDaysLeft !== null ? ` (${b.trialDaysLeft} days left)` : ''}. `
              : null}
            This workspace is billed through Hive, together with your other WRKRBZ apps. Plan changes, payment details and invoices are
            managed in Hive.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ProjectUsage b={b} />
          <ul className="space-y-2 text-sm">
            {(billing.isPaidPlan(b.plan) ? billing.PLANS[b.plan].highlights : []).map((h) => (
              <li key={h} className="flex gap-2">
                <Check className="mt-0.5 size-4 shrink-0 text-success" aria-hidden />
                {h}
              </li>
            ))}
          </ul>
          {addons.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-muted-foreground">Add-ons:</span>
              {addons.map((a) => (
                <Badge key={a}>{a}</Badge>
              ))}
            </div>
          )}
        </CardContent>
        <CardFooter className="flex-col items-start gap-2">
          {billingUrl ? (
            <Button asChild variant={b.plan === 'premium' ? 'outline' : 'primary'}>
              <a href={billingUrl}>
                Manage in Hive <ArrowUpRight />
              </a>
            </Button>
          ) : (
            <p className="text-sm text-muted-foreground">Manage this plan in Hive.</p>
          )}
          {b.plan !== 'premium' && <p className="text-xs text-muted-foreground">Upgrade in Hive for unlimited projects, budgets, bids, integrations and the API.</p>}
        </CardFooter>
      </Card>
    </div>
  );
}

function CurrentPlanCard({ b }: { b: Billing }) {
  const toast = useToast();
  const portal = trpc.workspace.billing.portal.useMutation({
    onSuccess: ({ url }) => window.location.assign(url),
    onError: (err) => toast.error('Could not open billing', errorMessage(err)),
  });
  const state = STATE_LABEL[b.state] ?? STATE_LABEL.active!;
  const limit = b.projectsPerYear;
  const used = b.projectsInWindow;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {b.planName} plan <Status tone={statusTone(b.state)}>{state.label}</Status>
        </CardTitle>
        <CardDescription>
          {b.state === 'trialing' && b.trialEndsAt
            ? `Full Premium access until ${formatDate(b.trialEndsAt)}${b.trialDaysLeft !== null ? ` (${b.trialDaysLeft} days left)` : ''}.`
            : b.cancelAtPeriodEnd && b.currentPeriodEnd
              ? `Cancels on ${formatDate(b.currentPeriodEnd)}.`
              : b.currentPeriodEnd
                ? `Renews on ${formatDate(b.currentPeriodEnd)}.`
                : b.state === 'inactive'
                  ? 'The workspace is read-only until you choose a plan. Nothing has been deleted.'
                  : null}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Projects created in the last 12 months</span>
            <span className="font-medium">
              {used}
              {limit !== null ? ` of ${limit}` : ' (unlimited)'}
            </span>
          </div>
          {limit !== null && (
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuemin={0} aria-valuemax={limit} aria-valuenow={used}>
              <div className={cn('h-full', used >= limit ? 'bg-destructive' : 'bg-foreground')} style={{ width: `${Math.min(100, (used / limit) * 100)}%` }} />
            </div>
          )}
        </div>
      </CardContent>
      {b.hasCustomer && b.canManage && (
        <CardFooter>
          <Button variant="outline" onClick={() => portal.mutate()} loading={portal.isPending}>
            <CreditCard /> Manage billing
          </Button>
        </CardFooter>
      )}
    </Card>
  );
}

function PlanCard({ plan, b }: { plan: Plan; b: Billing }) {
  const toast = useToast();
  const utils = trpc.useUtils();
  const checkout = trpc.workspace.billing.checkout.useMutation({
    onSuccess: ({ url }) => {
      if (url) {
        window.location.assign(url);
        return;
      }
      toast.success(`Switching to ${plan.name}`, 'The change is prorated on your next invoice.');
      setTimeout(() => void utils.workspace.billing.get.invalidate(), 2500);
    },
    onError: (err) => toast.error('Could not change plan', errorMessage(err)),
  });

  const isCurrent = b.plan === plan.id && b.state !== 'inactive' && !b.cancelAtPeriodEnd;
  const rank = { trial: 0, basic: 1, premium: 2 } as const;
  const upgrade = rank[plan.id] > rank[b.plan as keyof typeof rank];
  const label = isCurrent ? 'Current plan' : b.hasSubscription && b.state !== 'inactive' ? (upgrade ? `Upgrade to ${plan.name}` : `Switch to ${plan.name}`) : `Start with ${plan.name}`;
  const overLimit = plan.projectsPerYear !== null && b.projectsInWindow > plan.projectsPerYear;

  return (
    <Card className={cn('flex flex-col', plan.badge && 'ring-2 ring-primary')}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {plan.name}
          {plan.badge && <Badge variant="primary">{plan.badge}</Badge>}
        </CardTitle>
        <CardDescription>{plan.tagline}</CardDescription>
        <div className="pt-2">
          <span className="text-3xl font-semibold">{billing.formatPrice(plan)}</span>
          <span className="ml-1 text-sm text-muted-foreground">per company / month</span>
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        <ul className="space-y-2 text-sm">
          {plan.highlights.map((h) => (
            <li key={h} className="flex gap-2">
              <Check className="mt-0.5 size-4 shrink-0 text-success" aria-hidden />
              {h}
            </li>
          ))}
        </ul>
        {overLimit && !isCurrent && (
          <Alert variant="warning" className="mt-4">
            You created {b.projectsInWindow} projects in the last 12 months. On {plan.name} existing projects stay, but new ones are blocked
            until you are under {plan.projectsPerYear}. Budgets, change orders and invoicing become unavailable (data is kept).
          </Alert>
        )}
      </CardContent>
      <CardFooter className="flex-col items-stretch gap-2">
        <Button
          variant={isCurrent ? 'outline' : plan.badge ? 'primary' : 'secondary'}
          disabled={isCurrent || !b.canManage || !b.configured}
          loading={checkout.isPending}
          onClick={() => checkout.mutate({ plan: plan.id })}
        >
          {label}
        </Button>
        {!isCurrent && !b.canManage && <p className="text-center text-xs text-muted-foreground">Only workspace owners can change the plan.</p>}
        {!isCurrent && b.canManage && !b.configured && <p className="text-center text-xs text-muted-foreground">Online billing is not configured on this server.</p>}
      </CardFooter>
    </Card>
  );
}
